/**
 * Recruitment → Email Settings page.
 *
 * Two panels:
 *   1. HR CC & reply-to controls — set the addresses that get copied on
 *      every candidate email, and the master auto-send switch.
 *   2. Templates table — edit subject/body, toggle the auto-send checkbox
 *      so the rejection/shortlist email fires whenever the candidate hits
 *      that stage.
 *
 * Strict tenant isolation: every API call is scoped by the JWT's company.
 */
import { useEffect, useState } from "react";
import AppShell from "../components/AppShell";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";

export default function RecruitmentSettings() {
  const [settings, setSettings] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingTpl, setEditingTpl] = useState(null);
  const [hrCcInput, setHrCcInput] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [s, t] = await Promise.all([
        api.get("/recruitment/settings"),
        api.get("/recruitment/email-templates"),
      ]);
      setSettings(s.data);
      setHrCcInput((s.data.hr_cc_emails || []).join(", "));
      setTemplates(t.data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Failed to load settings");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const saveSettings = async () => {
    setSaving(true);
    const cc = hrCcInput
      .split(/[,;\n]/)
      .map(s => s.trim().toLowerCase())
      .filter(Boolean);
    try {
      const { data } = await api.put("/recruitment/settings", {
        ...settings, hr_cc_emails: cc,
      });
      setSettings(data);
      setHrCcInput((data.hr_cc_emails || []).join(", "));
      toast.success("Settings saved", { duration: 4000 });
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  const toggleAutoSend = async (tpl, next) => {
    try {
      const { data } = await api.put(`/recruitment/email-templates/${tpl.id}`, {
        auto_send_on_stage: next,
      });
      setTemplates(ts => ts.map(t => t.id === tpl.id ? data : t));
      toast.success(`Auto-send ${next ? "enabled" : "disabled"} for "${tpl.name}"`);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Update failed");
    }
  };

  if (loading) {
    return <AppShell><div className="p-8 text-zinc-500">Loading…</div></AppShell>;
  }

  return (
    <AppShell>
      <div className="max-w-4xl mx-auto p-6 space-y-6" data-testid="recruit-settings-page">
        <header>
          <h1 className="text-2xl font-semibold tracking-tight">Recruitment email settings</h1>
          <p className="text-sm text-zinc-500 mt-1">
            Controls who gets copied on candidate emails and which messages auto-send when stages change.
            All settings are isolated to this tenant.
          </p>
        </header>

        {/* HR CC + auto-send master */}
        <Card className="p-6 space-y-4" data-testid="recruit-settings-cc">
          <div>
            <Label className="text-sm font-medium">HR CC addresses</Label>
            <p className="text-xs text-zinc-500 mt-0.5 mb-2">
              Every candidate email — manual or auto-sent — will be CC'd to these addresses.
              Use commas or semicolons to separate.
            </p>
            <Input
              value={hrCcInput}
              onChange={(e) => setHrCcInput(e.target.value)}
              placeholder="hr@thegoodmen.in, records@thegoodmen.in"
              data-testid="hr-cc-input"
            />
            {settings.hr_cc_emails?.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {settings.hr_cc_emails.map(e => (
                  <span key={e} className="text-[11px] px-2 py-0.5 bg-zinc-100 text-zinc-700 rounded">{e}</span>
                ))}
              </div>
            )}
          </div>

          <div className="flex items-center justify-between pt-3 border-t border-zinc-100">
            <div>
              <Label className="text-sm font-medium">Reply-To</Label>
              <p className="text-xs text-zinc-500 mt-0.5">
                The "Reply-To" header on outgoing emails is always set to the logged-in recruiter's email,
                so candidate responses go straight to the person who sent the email.
              </p>
            </div>
            <span className="text-xs text-zinc-500 italic">Recruiter's own email</span>
          </div>

          <div className="flex items-center justify-between pt-3 border-t border-zinc-100">
            <div>
              <Label className="text-sm font-medium">Auto-send on stage change (master switch)</Label>
              <p className="text-xs text-zinc-500 mt-0.5">
                Enable individual templates below. Turn this off globally to pause all auto-sends company-wide.
              </p>
            </div>
            <Switch
              checked={settings.auto_send_enabled}
              onCheckedChange={(v) => setSettings(s => ({ ...s, auto_send_enabled: v }))}
              data-testid="auto-send-master"
            />
          </div>

          <div className="flex justify-end pt-2">
            <Button onClick={saveSettings} disabled={saving} data-testid="recruit-settings-save">
              {saving ? "Saving…" : "Save settings"}
            </Button>
          </div>
        </Card>

        {/* Templates table */}
        <Card className="p-6" data-testid="recruit-settings-templates">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-base font-semibold">Email templates</h2>
              <p className="text-xs text-zinc-500 mt-0.5">
                5 system defaults seeded for your company. Edit subject/body or toggle auto-send.
              </p>
            </div>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wide text-zinc-500 border-b">
                <th className="pb-2 font-medium">Template</th>
                <th className="pb-2 font-medium">Stage</th>
                <th className="pb-2 font-medium text-center">Auto-send</th>
                <th className="pb-2 font-medium"></th>
              </tr>
            </thead>
            <tbody>
              {templates.map(t => (
                <tr key={t.id} className="border-b last:border-0">
                  <td className="py-3 pr-2">
                    <div className="font-medium">{t.name}</div>
                    <div className="text-xs text-zinc-500 truncate max-w-md">{t.subject}</div>
                  </td>
                  <td className="py-3 pr-2">
                    <span className="text-xs px-2 py-0.5 bg-zinc-100 rounded capitalize">
                      {t.stage_hint || "—"}
                    </span>
                  </td>
                  <td className="py-3 pr-2 text-center">
                    <Switch
                      checked={!!t.auto_send_on_stage}
                      onCheckedChange={(v) => toggleAutoSend(t, v)}
                      data-testid={`auto-send-${t.key}`}
                    />
                  </td>
                  <td className="py-3 text-right">
                    <Button size="sm" variant="outline" onClick={() => setEditingTpl(t)} data-testid={`edit-tpl-${t.key}`}>
                      Edit
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      </div>

      {editingTpl && (
        <TemplateEditor
          tpl={editingTpl}
          onClose={() => setEditingTpl(null)}
          onSaved={(updated) => {
            setTemplates(ts => ts.map(t => t.id === updated.id ? updated : t));
            setEditingTpl(null);
          }}
        />
      )}
    </AppShell>
  );
}

function TemplateEditor({ tpl, onClose, onSaved }) {
  const [name, setName] = useState(tpl.name);
  const [subject, setSubject] = useState(tpl.subject);
  const [body, setBody] = useState(tpl.body);
  const [saving, setSaving] = useState(false);

  const previewCtx = {
    candidate_name: "Manik Giram",
    candidate_first_name: "Manik",
    role_title: "Desktop Support Engineer",
    company_name: "The Good Men Enterprise",
    recruiter_name: "Priya Sharma",
    interview_date: "Mon 9 Jun 2026",
    interview_time: "10:30 AM IST",
    interview_mode: "Video call",
    interview_location: "https://meet.google.com/abc-defg",
  };
  const sub = (s) => (s || "").replace(/\{([a-z_][a-z0-9_]*)\}/g, (m, k) => previewCtx[k] || m);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/recruitment/email-templates/${tpl.id}`, {
        name, subject, body,
      });
      toast.success("Template saved");
      onSaved(data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="tpl-editor-dialog">
        <DialogHeader>
          <DialogTitle>Edit template — {tpl.name}</DialogTitle>
          <DialogDescription>
            Customize the subject, body, and merge fields. Changes apply only to this tenant.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Display name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="tpl-name" />
          </div>
          <div>
            <Label className="text-xs">Subject</Label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="tpl-subject" />
            <div className="text-[11px] text-zinc-500 mt-1">Preview: <strong>{sub(subject)}</strong></div>
          </div>
          <div>
            <Label className="text-xs">Body (Markdown — use **bold**, blank line = new paragraph)</Label>
            <Textarea rows={12} value={body} onChange={(e) => setBody(e.target.value)} className="font-mono text-sm" data-testid="tpl-body" />
          </div>
          <div className="border border-zinc-200 rounded p-3 bg-zinc-50">
            <div className="text-[11px] uppercase tracking-wide text-zinc-500 mb-1">Preview with sample data</div>
            <div className="text-sm whitespace-pre-wrap text-zinc-700 leading-relaxed">
              {sub(body)}
            </div>
          </div>
          <div className="text-[11px] text-zinc-500">
            Merge fields: <code>{"{candidate_name} {role_title} {company_name} {recruiter_name} {interview_date} {interview_time} {interview_mode} {interview_location}"}</code>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="tpl-save">{saving ? "Saving…" : "Save template"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
